import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isFunction } from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { DefaultButton } from '@fluentui/react';

import { ConfirmDialog } from './confirm-dialog';
import { useCreatePromise } from '../use-create-promise';

const SecondaryButton = (props) => {
  const {
    buttonKey,
    className,
    content,
    dismissDialog,
    onClick: originalOnClick,
    shouldDismissDialog,
  } = props;

  const onClick = useCallback((...params) => {
    if (shouldDismissDialog) {
      dismissDialog(buttonKey);
    }

    return originalOnClick(...params);
  }, [buttonKey, dismissDialog, originalOnClick, shouldDismissDialog]);

  return (
    <DefaultButton
      className={classNames(className, 'dialog-dismiss')}
      onClick={onClick}
    >
      {content}
    </DefaultButton>
  );
};

SecondaryButton.propTypes = {
  buttonKey: PropTypes.string.isRequired,
  content: PropTypes.node.isRequired,
  className: PropTypes.string,
  dismissDialog: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  shouldDismissDialog: PropTypes.bool,
};

SecondaryButton.defaultProps = {
  className: null,
  shouldDismissDialog: true,
};

export function useConfirmDialog(defaultProps, instrumentationInfo = {}) {
  const [additionalProps, setAdditionalProps] = useState({ hidden: true });
  const {
    content,
    onConfirm,
    onCancel: originalOnCancel,
    // onDismiss is used to controll the top-right close button and light dismiss
    // if onDismiss parameter is not provided, keep same behavior as cancel button
    onDismiss: originalOnDismiss,
    secondaryButtons = [],
    onDisplay,
    ...passThroughProps
  } = {
    ...defaultProps,
    ...additionalProps,
  };

  const {
    activity,
    dialogName,
  } = instrumentationInfo;

  const showDialog = useCallback((periodProps = {}) => {
    if (activity) {
      activity.trace(`Showing dialog "${dialogName}".`);
    }

    setAdditionalProps({
      ...periodProps,
      hidden: false,
    });

    if (onDisplay && isFunction(onDisplay)) {
      onDisplay();
    }
  }, [setAdditionalProps, activity, dialogName, onDisplay]);

  const dismissDialog = useCallback((buttonKey) => {
    if (activity) {
      activity.trace(`Dialog "${dialogName}" dismissed with button "${buttonKey}".`);
    }

    // Keep additionalProps here to avoid the dialog renders again
    //   without additionalProps before hidding.
    setAdditionalProps({
      ...additionalProps,
      hidden: true,
    });
  }, [additionalProps, setAdditionalProps, activity, dialogName]);

  const internalOnCancel = useCallback(() => {
    dismissDialog('cancel');

    if (originalOnCancel) {
      originalOnCancel();
    }
  }, [dismissDialog, originalOnCancel]);

  const internalOnDismiss = useCallback(() => {
    dismissDialog('close');

    if (originalOnDismiss) {
      originalOnDismiss();
    }
  }, [dismissDialog, originalOnDismiss]);

  const onConfirmClicked = useCallback(() => {
    dismissDialog('confirm');

    onConfirm();
  }, [onConfirm, dismissDialog]);

  return [
    (
      <ConfirmDialog
        {...passThroughProps}

        onConfirm={onConfirmClicked}

        secondaryButtons={secondaryButtons.map(buttonProps => (
          <SecondaryButton
            {...buttonProps}
            buttonKey={buttonProps.key}
            dismissDialog={dismissDialog}
          />
        ))}

        onCancel={internalOnCancel}
        onDismiss={originalOnDismiss && internalOnDismiss}
      >
        {content}
      </ConfirmDialog>
    ),
    showDialog,
    !passThroughProps.hidden,
  ];
}

export function useConfirmDialogWithPromise(props) {
  const { current: statics } = useRef({});
  const showDialogAdapter = useCallback((options, periodProps) => {
    statics.showDialog(periodProps);
  }, [statics]);

  const [, {
    createPromise,
    resolveLastCreatedPromise: resolvePromise,
    rejectLastCreatedPromise: rejectPromise,
  }] = useCreatePromise(showDialogAdapter);

  const [dialog, originalShowDialog, visible] = useConfirmDialog({
    ...props,
    onConfirm: resolvePromise,
    onCancel: rejectPromise,
  });

  useEffect(() => {
    statics.showDialog = originalShowDialog;
  }, [originalShowDialog, statics]);

  const { onConfirm, onCancel } = props;

  const showDialogWithPromise = useCallback((periodProps) => {
    if (statics.promise) {
      return Promise.reject();
    }

    statics.promise = createPromise(periodProps);

    return statics.promise
      .then(
        onConfirm,
        () => {
          if (onCancel) {
            onCancel();
          }

          return Promise.reject();
        }
      )
      .finally(() => {
        statics.promise = null;
      });
  }, [statics, createPromise, onConfirm, onCancel]);

  return [dialog, showDialogWithPromise, visible];
}
